import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * MenuItem — a row inside a dropdown menu or popover.
 *
 * Standard pattern: full-width clickable row with left-aligned icon + label
 * (`w-full px-3 py-2 text-left text-sm flex items-center gap-2.5`). Two tones
 * cover the common cases:
 * - `default` (ink-700 idle, ink-50 hover) — most menu actions
 * - `danger` (red-600 idle, red-50 hover) — destructive actions (Delete, Remove)
 *
 * Consumers can override the icon's tint by passing a colored icon as children
 * — the MenuItem itself only styles the text and hover background.
 *
 * Not yet a full Menu primitive (no keyboard navigation, no role="menu"). When
 * a real Menu/DropdownMenu lands, MenuItem becomes its row implementation.
 */
export const menuItemVariants = cva(
  'w-full px-3 py-2 text-left text-sm flex items-center gap-2.5 transition-colors ' +
    'focus:outline-none focus-visible:bg-ink-50 ' +
    'disabled:opacity-50 disabled:pointer-events-none',
  {
    variants: {
      tone: {
        default: 'text-ink-700 hover:bg-ink-50',
        danger: 'text-danger-600 hover:bg-danger-50',
      },
    },
    defaultVariants: {
      tone: 'default',
    },
  }
);

export interface MenuItemProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'role'>,
    VariantProps<typeof menuItemVariants> {}

export const MenuItem = forwardRef<HTMLButtonElement, MenuItemProps>(
  ({ className, tone, type = 'button', children, ...props }, ref) => (
    <button
      ref={ref}
      type={type}
      role="menuitem"
      className={cn(menuItemVariants({ tone }), className)}
      {...props}
    >
      {children}
    </button>
  )
);
MenuItem.displayName = 'MenuItem';
